import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { reset } from 'redux-form';
import { toast } from "react-toastify";
import { createInvestAccount, patchInvestAccount, resetFlags } from '../../../redux/actions/AccountAction';
import SelectAccountType from './selectAccountType';
import ConnectBankAccount from './connectBankAccount';
import RiskReturnExpectation from './riskReturnExpectation';
import ProposedPortfolio from './proposedPortfolio';
import AccountOwnerNameInfo from './accountOwnerNameInfo';
import SelectBankAccount from './selectBankAccount';
import TransferFund from './transferFund';
import AccountOwnerMaritalInfo from './accountOwnerMaritalInfo';
import AccountOwnerDOB from './accountOwnerDOB';
import AccountOwnerAddressInfo from './accountOwnerAddressInfo';
import AccountOwnerPrevAddressInfo from './accountOwnerPrevAddressInfo';
import AccountOwnerMailingInfo from './accountOwnerMailingInfo';
import AccountOwnerImmigrationInfo from './accountOwnerImmigrationInfo';
import AccountOwnerCitizenDoc from './accountOwnerCitizenDoc';
import AccountOwnerEmploymentStatus from './accountOwnerEmploymentStatus';
import AccountOwnerEmployerInfo from './accountOwnerEmployerInfo';
import AccountOwnerFinancialInfo from './accountOwnerFinancialInfo';
import AccountOwnerSocialSecurityNumber from './accountOwnerSocialSecurityNumber';
import AccountOwnerRegulatoryDisclosures1 from './accountOwnerRegulatoryDisclosures1';
import AccountOwnerRegulatoryDisclosures2 from './accountOwnerRegulatoryDisclosures2';
import AccountOwnerUploadApprovalLetter from './accountOwnerUploadApprovalLetter';
import AccountOwnerStockTradingRestriction from './accountOwnerStockTradingRestriction';
import ElectronicSignature from './electronicSignature';

const AddAccount = ({
    accountErrors,
    accountFlags,
    resetFlags,
    createInvestAccount,
    patchInvestAccount,
    history,
    investAccount,
    resetForm,
    plaidAccounts
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [accountInfo, setAccountInfo] = useState({});
    const [processing, setProcessing] = useState(false);
    const [second, setSecond] = useState(false);

    const skipPatchFor = [13, 20, 29, 36];
    const formsToReset = [
        'AccountOwnerNameInfoForm',
        'AccountOwnerMaritalInfoForm',
        'AccountOwnerDOBForm',
        'AccountOwnerAddressInfoForm',
        'AccountOwnerPrevAddressInfoForm',
        'AccountOwnerMailingInfoForm',
        'AccountOwnerImmigrationInfoForm',
        'AccountOwnerEmploymentStatusForm',
        'AccountOwnerEmployerInfoForm',
        'AccountOwnerFinancialInfoForm',
        'AccountOwnerSocialSecurityNumberForm',
    ];
    const isJoint = (accountInfo && accountInfo.ownership && accountInfo.ownership === 'joint') ||
        (investAccount && investAccount.ownership && investAccount.ownership === 'joint');

    useEffect(() => {
        setInterval(() => {
            changeStep();
        }, 100);
        if (accountErrors && accountErrors.createInvestAccount) {
            toast.error(accountErrors.createInvestAccount);
            setProcessing(false);
            resetFlags();
        }

        if (accountErrors && accountErrors.patchInvestAccount) {
            toast.error(accountErrors.patchInvestAccount);
            setProcessing(false);
            resetFlags();
        }

        if (accountFlags && accountFlags.createInvestAccountSuccess) {
            resetFlags();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountErrors, accountFlags]);

    const nextStep = (data, skip) => {
        const redirectToNextOnSuccess = () => {
            setProcessing(false);
            resetFlags();

            // Case for opening Joint account
            if (currentStep === 21) {
                if (!isJoint) {
                    setCurrentStep(currentStep + 1);
                    return;
                } else {
                    // clear forms
                    formsToReset.forEach(form => {
                        resetForm(form);
                    });
                }
            }

            // Not broker then move to Electronic signature
            if (currentStep === 35 && !data.secondary_account_owner_is_broker_dealer_checked) {
                setCurrentStep(37)
                return;
            }

            // Move to Electronic signature
            if (currentStep === 36) {
                setCurrentStep(37)
                return;
            }

            // Account setup completed and move to refer friend
            if ((currentStep === 22 && !isJoint) || (currentStep === 37 && isJoint)) {
                history.push('/refer-friend');
                return;
            }

            setCurrentStep(skip ? (currentStep + (skip + 1)) : (currentStep + 1));
        };

        setAccountInfo({ ...accountInfo, ...data });
        if (!data || !Object.keys(data).length) {
            redirectToNextOnSuccess();
            return;
        }

        setProcessing(true);
        if (skipPatchFor.includes(currentStep)) {
            redirectToNextOnSuccess();
            return;
        }

        if (currentStep === 0) {
            if (!investAccount) {
                createInvestAccount(data, redirectToNextOnSuccess);
            } else {
                data.id = investAccount.id;
                patchInvestAccount(data, redirectToNextOnSuccess);
            }
        } else {
            data.id = investAccount.id;
            patchInvestAccount(data, redirectToNextOnSuccess);
        }
    };

    const changeStep = () => {
        let step =localStorage.getItem('navigation');
        if (step === "select") {
            setCurrentStep(0);
        } else if(step === "risk"){
            setCurrentStep(1);
        } else if(step === "portfolio"){
            setCurrentStep(2);
        } else if(step === "fund"){
            setCurrentStep(4);
        } else if(step === "primary"){
            setCurrentStep(6);
            setSecond(false);
        } else if(step === "secondary"){
            setCurrentStep(6);
            setSecond(true);
        } else if(step === "sign"){
            setCurrentStep(37);
        } else if(step === "refer"){
            history.push('/refer-friend');
        }
        localStorage.setItem('navigation', '');
    };

    const prevStep = (skip) => {
        if (currentStep === 0) {
            history.push('verified');
            return;
        };
        if (currentStep === 37 && isJoint &&
            (!accountInfo.secondary_account_owner_is_broker_dealer_checked)) {
            setCurrentStep(35);
            return;
        }
        setCurrentStep(skip ? ((currentStep - (skip + 1))) : (currentStep - 1));
    };

    return (
        <React.Fragment>
            <SelectAccountType
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 0}
                processing={processing}
            />
            <RiskReturnExpectation
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 1}
                processing={processing}
            />
            <ProposedPortfolio
                nextStep={nextStep}
                prevStep={prevStep}
                expectation={accountInfo.risk_return_expectation}
                active={currentStep === 2}
                processing={processing}
                hasPlaidAccountConnected={plaidAccounts && plaidAccounts.length}
            />
            <ConnectBankAccount
                nextStep={nextStep}
                prevStep={prevStep}
                active={currentStep === 3}
                currentStep={currentStep}
                processing={processing}
            />
            <SelectBankAccount
                nextStep={nextStep}
                prevStep={prevStep}
                active={currentStep === 4}
                currentStep={currentStep}
                processing={processing}
                hasPlaidAccountConnected={plaidAccounts && plaidAccounts.length}
            />
            <TransferFund
                nextStep={nextStep}
                prevStep={prevStep}
                processing={processing}
                investAccount={investAccount}
                active={currentStep === 5}
            />
            <AccountOwnerNameInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 6 || (isJoint && currentStep === 22)}
                processing={processing}
                second={second}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerMaritalInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 7 || currentStep === 23}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerDOB
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 8 || currentStep === 24}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerAddressInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 9 || currentStep === 25}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerPrevAddressInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 10 || currentStep === 26}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerMailingInfo
                nextStep={nextStep}
                prevStep={prevStep}
                accountInfo={accountInfo}
                investAccount={investAccount}
                livingSinceMoreThanYear={(isJoint && currentStep > 21) ?
                    accountInfo.is_secondary_account_owner_living_for_more_than_one_year :
                    accountInfo.is_primary_account_owner_living_for_more_than_one_year}
                active={currentStep === 11 || currentStep === 27}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerImmigrationInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 12 || currentStep === 28}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerCitizenDoc
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 13 || currentStep === 29}
                immigrationStatus={(isJoint && currentStep > 21) ?
                    accountInfo.secondary_account_owner_us_immigration_status :
                    accountInfo.primary_account_owner_us_immigration_status}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerEmploymentStatus
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 14 || currentStep === 30}
                isUsCitizen={(isJoint && currentStep > 21) ?
                    accountInfo.secondary_account_owner_us_immigration_status === 'US Citizen' :
                    accountInfo.primary_account_owner_us_immigration_status === 'US Citizen'}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerEmployerInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 15 || currentStep === 31}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerFinancialInfo
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 16 || currentStep === 32}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerSocialSecurityNumber
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 17 || currentStep === 33}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerRegulatoryDisclosures1
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 18 || currentStep === 34}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerRegulatoryDisclosures2
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 19 || currentStep === 35}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerUploadApprovalLetter
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                active={currentStep === 20 || currentStep === 36}
                processing={processing}
                secondary={isJoint && currentStep > 21}
            />
            <AccountOwnerStockTradingRestriction
                nextStep={nextStep}
                prevStep={prevStep}
                investAccount={investAccount}
                processing={processing}
                active={currentStep === 21}
                brokerDealerChecked={accountInfo.primary_account_owner_is_broker_dealer_checked}
            />
            <ElectronicSignature
                nextStep={nextStep}
                prevStep={prevStep}
                processing={processing}
                active={isJoint ? currentStep === 37 : currentStep === 22}
            />

        </React.Fragment>
    )
};

const mapStateToProps = state => ({
    accountErrors: state.Account.errors,
    accountFlags: state.Account.flags,
    investAccount: state.Account.investAccount,
    plaidAccounts: state.Account.plaidAccounts,
});

const mapDispatchToProps = dispatch => ({
    createInvestAccount: (payload, callback) => {
        dispatch(createInvestAccount(payload, callback))
    },
    patchInvestAccount: (payload, callback) => {
        dispatch(patchInvestAccount(payload, callback))
    },
    resetFlags: () => {
        dispatch(resetFlags())
    },
    resetForm: (name) => {
        dispatch(reset(name))
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddAccount);
